import { useEffect, useMemo, useRef } from 'react';

import { RouterProvider } from '@tanstack/react-router';

import casinoConfig from '@dcentralexpedientedigital/config';
import { ElantilApiProvider } from '@dcentralexpedientedigital/fe-cms-api';
import {
    PlatformApiProvider as PamApiProvider,
    CubeApiProvider,
} from '@dcentralexpedientedigital/fe-pam-api';
import { PlatformApiProvider } from '@dcentralexpedientedigital/fe-platform-api';
import { useAuth } from '@dcentralexpedientedigital/fe-shared/hooks/use-auth';
import { useGTM } from '@dcentralexpedientedigital/fe-shared/hooks/use-gtm';
import { useConfig } from '@dcentralexpedientedigital/fe-ui/hooks/use-config';

import { DynamicProviderWrapper } from './components/dynamic-provider-wrapper';
import { IntlProvider } from './components/intl-provider';
import { providerRegistry } from './provider-registry';
import { router } from './routes.tsx';
import type { ProviderEntry } from './types.ts';
const config = {
    directus: {
        baseUrl: casinoConfig.cmsBaseUrl,
        token: casinoConfig.cmsApiToken,
    },
};

const platformConfigBase = {
    platform: {
        baseUrl: casinoConfig.pamBaseUrl,
        xTenantId: casinoConfig.tenantId,
    },
    dataPlatform: {
        dlstBaseUrl: casinoConfig.dataPlatformDlstBaseUrl,
        campaignBaseUrl: casinoConfig.dataPlatformCampaignBaseUrl,
    },
    isLoggedIn: false,
};

const cubeApiConfig = {
    apiUrl: casinoConfig.dataPlatformSemlyrBaseUrl,
    tenantId: casinoConfig.tenantId,
};

export function MainWithAuth() {
    const { auth, isAuthResolved } = useAuth();
    const { name: brand } = useConfig();
    const { trackEvent } = useGTM();
    const trackedRef = useRef(false);

    useEffect(() => {
        if (
            isAuthResolved &&
            auth?.isAuthenticated &&
            auth?.user?.profile?.sub &&
            !trackedRef.current
        ) {
            trackedRef.current = true;
            trackEvent({
                event: 'user_context',
                payload: {
                    authStatus: 'logged_in',
                    brand,
                },
            });
        }
    }, [isAuthResolved, auth?.isAuthenticated, auth?.user?.profile?.sub]);

    const pamPlatformConfig = useMemo(
        () => ({
            ...platformConfigBase,
            isLoggedIn: auth.isAuthenticated,
            userId: auth.user?.profile.sub,
        }),
        [auth.isAuthenticated, auth.user?.profile.sub],
    );

    const platformApiConfig = useMemo(
        () => ({
            baseUrl: casinoConfig.platformApiBaseUrl,
            tenantId: casinoConfig.tenantId,
        }),
        [casinoConfig.platformApiBaseUrl, casinoConfig.tenantId],
    );

    const dgaProvidersConfig = useMemo(() => {
        const providers: ProviderEntry[] = [];

        if (casinoConfig.dgaProviders) {
            casinoConfig.dgaProviders.forEach((entry) => {
                const currentConfig = entry.config;
                const LazyProviderComponent = providerRegistry[entry.integration];

                if (!LazyProviderComponent) {
                    console.warn(
                        `No lazy provider component found in registry for integration: "${entry.integration}". Skipping.`,
                    );
                    return;
                }

                if (
                    !currentConfig ||
                    !currentConfig.casinoId ||
                    !currentConfig.serverUrl ||
                    !currentConfig.scriptUrl
                ) {
                    console.warn(
                        `Incomplete or invalid DGA config for integration "${entry.integration}". Missing casinoId, serverUrl, or scriptUrl. Skipping.`,
                        currentConfig,
                    );
                    return;
                }

                providers.push({
                    component: LazyProviderComponent,
                    config: currentConfig,
                    key: entry.integration,
                });
            });
        }
        return providers;
    }, []);

    const mainAppContent = (
        <IntlProvider>
            <RouterProvider router={router} />
        </IntlProvider>
    );

    return (
        <ElantilApiProvider config={config}>
            <DynamicProviderWrapper providers={dgaProvidersConfig}>
                <PamApiProvider config={pamPlatformConfig}>
                    <PlatformApiProvider config={platformApiConfig}>
                        <CubeApiProvider config={cubeApiConfig}>{mainAppContent}</CubeApiProvider>
                    </PlatformApiProvider>
                </PamApiProvider>
            </DynamicProviderWrapper>
        </ElantilApiProvider>
    );
}
